// Import interfaces from types file
import {KanjiData, KanjiLink, KanjiNode} from './types.js';

// Import functions from other files
import {createGraph} from './create-graph.js';
import {interactWithKanji} from './kanji-detail.js';
import {initializeSearch} from './kanji-search.js';

// Define shared state that will be initialized by createGraph
export const state: {
    width: number;
    height: number;
    svg: any;
    container: any;
    zoomBehavior: any;
    simulation: any;
    highlightKanjiSearch: ((searchTerm: string) => void) | null;
    resetKanjiHighlights: (() => void) | null;
    nodes: KanjiNode[];
    links: KanjiLink[];
    radicalToKanjiMap: Map<string, string[]>;
    minReferences: number;
    maxReferences: number;
} = {
    width: 0,
    height: 0,
    svg: null,
    container: null,
    zoomBehavior: null,
    simulation: null,
    highlightKanjiSearch: null,
    resetKanjiHighlights: null,
    nodes: [],
    links: [],
    radicalToKanjiMap: new Map<string, string[]>(),
    minReferences: 0,
    maxReferences: 1
};

// Function to initialize the overview panel with stats and random kanji
function initializeOverviewPanel(nodes: KanjiNode[], links: KanjiLink[]): void {
    // Update statistics only if not already populated server-side
    const totalKanjiCount = document.getElementById('totalKanjiCount');
    if (totalKanjiCount && totalKanjiCount.textContent === '0') {
        totalKanjiCount.textContent = nodes.length.toString();
    }

    const relationshipCount = document.getElementById('relationshipCount');
    if (relationshipCount && relationshipCount.textContent === '0') {
        relationshipCount.textContent = links.length.toString();
    }

    // Setup random kanji
    const randomKanjiContainer = document.getElementById('randomKanji');
    if (randomKanjiContainer) {
        const existingKanji = randomKanjiContainer.querySelectorAll('.random-kanji');

        if (existingKanji.length > 0) {
            // Server-rendered kanji exist, just add click handlers
            existingKanji.forEach(element => {
                const kanjiChar = element.getAttribute('data-kanji');
                if (kanjiChar) {
                    const node = nodes.find(n => n.kanji === kanjiChar);
                    if (node) {
                        element.addEventListener('click', (event) => {
                            event.preventDefault(); // Prevent full page navigation
                            interactWithKanji(node);
                        });
                    }
                }
            });
        } else {
            // No server-rendered kanji, generate client-side
            const getRandomElements = (arr: any[], count: number) => {
                return arr.sort(() => 0.5 - Math.random()).slice(0, Math.min(count, arr.length));
            };

            const randomSelection = getRandomElements(nodes, 8);

            randomSelection.forEach(node => {
                const kanjiElement = document.createElement('a');
                kanjiElement.href = `/kanji/${encodeURIComponent(node.kanji)}`;
                kanjiElement.className = 'random-kanji';
                kanjiElement.textContent = node.kanji;
                kanjiElement.setAttribute('data-kanji', node.kanji);
                kanjiElement.addEventListener('click', (event) => {
                    event.preventDefault(); // Prevent full page navigation
                    interactWithKanji(node);
                });
                randomKanjiContainer.appendChild(kanjiElement);
            });
        }
    }
    
    // Add event listener to close button to show overview again
    const closeButton = document.getElementById('closeDetail');
    if (closeButton) {
        closeButton.addEventListener('click', () => {
            const overviewPanel = document.getElementById('kanjiOverview');
            const detailPanel = document.getElementById('kanjiDetail');

            if (overviewPanel) overviewPanel.style.display = 'block';
            if (detailPanel) detailPanel.style.display = 'none';

            // Remove notification banner if it exists
            const banner = document.getElementById('graphNotificationBanner');
            if (banner) banner.remove();

            // Update URL without navigation
            history.pushState({ kanji: null }, '', '/kanji');

            // Reset highlights
            if (state.resetKanjiHighlights) {
                state.resetKanjiHighlights();
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', async function() {
    try {
        // Fetch kanji data
        const response = await fetch('/assets/etymology.json');
        if (!response.ok) {
            throw new Error(`Failed to fetch kanji data: ${response.status} ${response.statusText}`);
        }
        
        const allKanjiData: KanjiData[] = await response.json();
        
        // Filter entries with etymology
        const kanjiData = allKanjiData.filter(entry => entry.etymology);
        
        // Process kanji data to build graph
        const nodes: KanjiNode[] = [];
        const links: KanjiLink[] = [];
        const kanjiMap = new Map<string, KanjiData>();
        // Create a map to track which kanji are the source of which radicals
        const radicalToKanjiMap = new Map<string, string[]>();
        
        // First pass: create nodes for all kanji with etymology
        kanjiData.forEach(kanji => {
            // Create node object
            const node: KanjiNode = {
                id: kanji.entry,
                kanji: kanji.entry,
                furigana: kanji.furigana,
                meaning: kanji.meaning,
                etymology: kanji.etymology,
                references: 0, // Will count incoming references
                jouyou: kanji.jouyou || false, // Default to false if undefined
                jlpt_level: kanji.jlpt_level
            };
            
            // Add to map for quick lookup
            kanjiMap.set(kanji.entry, kanji);
            
            // Add to nodes array
            nodes.push(node);
        });

        // Build the radical to kanji mapping (only for radicals that don't exist as kanji)
        kanjiData.forEach(kanji => {
            if (kanji.as_a_radical && Array.isArray(kanji.as_a_radical)) {
                kanji.as_a_radical.forEach(radical => {
                    // Only map if the radical doesn't exist as a kanji entry itself
                    if (!kanjiMap.has(radical)) {
                        if (!radicalToKanjiMap.has(radical)) {
                            radicalToKanjiMap.set(radical, []);
                        }
                        radicalToKanjiMap.get(radical)?.push(kanji.entry);
                    }
                });
            }
        });
        
        // Second pass: extract relationships from etymology text
        kanjiData.forEach(kanji => {
            // Extract kanji references from etymology text using regex
            const pattern = /\{([^|]+)\|([^}]+)}/g;
            let match: RegExpExecArray | null;
            const referencedKanji = new Set<string>();
            
            while ((match = pattern.exec(kanji.etymology)) !== null) {
                const referencedChar = match[1];
                const type = match[2]; // C, CO, NGU, radical
                
                // Skip if it's the same kanji
                if (referencedChar === kanji.entry) continue;
                
                // Add to set to avoid duplicates
                referencedKanji.add(referencedChar);
                
                // If this is a radical reference, create links from this kanji to any source kanji of the radical
                if (type === 'radical' && radicalToKanjiMap.has(referencedChar)) {
                    radicalToKanjiMap.get(referencedChar)?.forEach(sourceKanji => {
                        // Skip self-references
                        if (sourceKanji !== kanji.entry) {
                            // Create a link from this kanji to the source kanji of the radical
                            links.push({
                                source: kanji.entry,
                                target: sourceKanji
                            });
                            
                            // Increment reference count for the target
                            const targetNode = nodes.find(node => node.id === sourceKanji);
                            if (targetNode) {
                                targetNode.references++;
                            }
                        }
                    });
                }
            }
            
            // Add kanji from as_a_radical if present
            if (kanji.as_a_radical && Array.isArray(kanji.as_a_radical)) {
                kanji.as_a_radical.forEach(radical => {
                    if (radical !== kanji.entry) {
                        referencedKanji.add(radical);
                    }
                });
            }
            
            // Create links for each reference
            referencedKanji.forEach(targetKanji => {
                // Check if the target kanji exists in our data
                if (kanjiMap.has(targetKanji)) {
                    links.push({
                        source: kanji.entry,
                        target: targetKanji
                    });
                    
                    // Increment reference count for the target
                    const targetNode = nodes.find(node => node.id === targetKanji);
                    if (targetNode) {
                        targetNode.references++;
                    }
                }
            });
        });
        
        // Store nodes and links in state for access by other components
        state.nodes = nodes;
        state.links = links;
        state.radicalToKanjiMap = radicalToKanjiMap;

        // Calculate min/max references for range-based sizing and coloring
        const refCounts = nodes.map(n => n.references);
        state.minReferences = Math.min(...refCounts);
        state.maxReferences = Math.max(...refCounts);
        
        // Create the graph visualization
        createGraph(nodes, links);

        // Initialize the overview panel
        initializeOverviewPanel(nodes, links);

        // If there's an initial kanji from SSR, show loading overlay and zoom after simulation settles
        const initialKanji = (window as any).INITIAL_KANJI;
        if (initialKanji) {
            const initialNode = nodes.find(node => node.kanji === initialKanji);
            const overlay = document.getElementById('graphLoadingOverlay');

            if (initialNode && state.simulation) {
                // Show loading overlay
                if (overlay) overlay.style.display = 'flex';

                // Wait for simulation to settle, then zoom
                state.simulation.on('tick.initial', () => {
                    if (state.simulation.alpha() < 0.1) {
                        state.simulation.on('tick.initial', null); // Remove listener

                        // Hide loading overlay
                        if (overlay) overlay.style.display = 'none';

                        // Zoom to the node
                        interactWithKanji(initialNode, false, false);
                    }
                });
            } else {
                // Kanji not found in graph - hide overlay and show banner
                if (overlay) overlay.style.display = 'none';

                // Create and show banner notification
                const graphContainer = document.getElementById('kanjiGraph');
                if (graphContainer) {
                    const banner = document.createElement('div');
                    banner.id = 'graphNotificationBanner';
                    banner.innerHTML = `
                        <div style="flex: 1;">This kanji is not included in the etymology graph</div>
                        <button style="background: none; border: none; color: inherit; cursor: pointer; font-size: 1.2rem; padding: 0 0.5rem;">&times;</button>
                    `;
                    banner.style.cssText = `
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background-color: rgba(255, 149, 0, 0.9);
                        color: white;
                        padding: 0.75rem 1rem;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        z-index: 50;
                        font-size: 0.9rem;
                    `;
                    const closeBtn = banner.querySelector('button');
                    if (closeBtn) {
                        closeBtn.addEventListener('click', () => banner.remove());
                    }
                    graphContainer.appendChild(banner);
                }
            }
            // Replace current state so back button works correctly
            history.replaceState({ kanji: initialKanji }, '', window.location.pathname);
        }

        // Handle browser back/forward navigation
        window.addEventListener('popstate', (event) => {
            const kanjiFromState = event.state?.kanji;
            if (kanjiFromState) {
                const node = nodes.find(n => n.kanji === kanjiFromState);
                if (node) {
                    interactWithKanji(node, true, false); // Show details but don't push state again
                }
            } else {
                // No kanji in state, show overview
                const overviewPanel = document.getElementById('kanjiOverview');
                const detailPanel = document.getElementById('kanjiDetail');
                if (overviewPanel) overviewPanel.style.display = 'block';
                if (detailPanel) detailPanel.style.display = 'none';
                if (state.resetKanjiHighlights) {
                    state.resetKanjiHighlights();
                }
            }
        });

        // Setup search functionality
        initializeSearch(state.nodes, state.resetKanjiHighlights);

        // Intercept clicks on kanji links in the sidebar to prevent full page navigation
        document.querySelector('.split-right')?.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            const link = target.closest('a[data-kanji], .related-kanji[data-kanji]');
            if (link) {
                event.preventDefault();
                const kanjiChar = link.getAttribute('data-kanji');
                if (kanjiChar) {
                    const node = nodes.find(n => n.kanji === kanjiChar);
                    if (node) {
                        interactWithKanji(node);
                    }
                }
            }
        });

        // Make global functions accessible
        (window as any).highlightKanji = (searchTerm: string) => {
            if (state.highlightKanjiSearch) {
                state.highlightKanjiSearch(searchTerm);
            }
        };
        (window as any).resetHighlights = () => {
            if (state.resetKanjiHighlights) {
                state.resetKanjiHighlights();
            }
        };
    } catch (error) {
        console.error("Error initializing kanji etymology graph:", error);
        const graphContainer = document.getElementById('kanjiGraph');
        if (graphContainer) {
            graphContainer.innerHTML = `<div class="error-message">Failed to load kanji data. Please try refreshing the page.</div>`;
        }
    }
});
